import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { resultsPath, homePath } from './franklyNetHost';
import { frameUrl } from './frameLinks';
import { useQuerySuggestions, SuggestionList } from './QuerySuggestions';
import { useSiteTheme, resolveThemeAssetUrl } from '../../data/siteTheme';

function RecommendedTile({ link, onOpen }) {
    return (
        <button
            type="button"
            onClick={() => onOpen(link.url)}
            title={link.subtitle || link.title}
            className="group relative h-[42px] w-full overflow-hidden rounded-[10px] border border-black/30 bg-black/30 text-left transition-transform hover:-translate-y-[1px]"
            style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.35)' }}
        >
            {link.thumbnailUrl && (
                <img
                    src={link.thumbnailUrl}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                    className="absolute inset-0 h-full w-full object-cover opacity-45 transition-opacity group-hover:opacity-65"
                />
            )}
            <span className="relative z-10 flex h-full items-center px-2 font-['Jersey_10'] text-[13px] uppercase leading-none tracking-[1px] text-white">
                <span className="truncate">{link.title}</span>
            </span>
        </button>
    );
}

// themeOverride is for the Housekeeping themes desk, which renders this strip
// against an unsaved draft.
function FranklyNetBanner({ query = '', recommended = [], onSearch, themeOverride = null }) {
    const [value, setValue] = useState(query);
    const navigate = useNavigate();

    const { suggestions, highlight, setHighlight, clear, onKeyDown } = useQuerySuggestions(value);

    const liveTheme = useSiteTheme();
    const theme = themeOverride || liveTheme;
    // This strip is part of AskFrank, not the main site, so it wears the
    // AskFrank grid rather than the website banner tile.
    const { askFrankLogo, askFrankTile } = theme.config;

    useEffect(() => {
        setValue(query);
        clear(query);
    }, [query, clear]);

    const run = (nextValue) => {
        const cleaned = String(nextValue || '').trim();
        if (cleaned.length < 2) return;
        clear(cleaned);
        if (onSearch) onSearch(cleaned);
        else navigate(resultsPath(cleaned));
    };

    const submit = (event) => {
        event.preventDefault();
        run(value);
    };

    const pick = (item) => {
        setValue(item);
        run(item);
    };

    const tiles = recommended.slice(0, 6);

    const openLink = (url) => {
        navigate(frameUrl(url));
    };

    return (
        <div
            className="w-full bg-repeat relative z-20"
            style={{
                backgroundImage: `url(${resolveThemeAssetUrl(askFrankTile.url)})`,
                boxShadow: '0 3px 0 0 rgba(0,0,0,0.25)',
                imageRendering: 'pixelated',
            }}
        >
            <div className="mx-auto flex w-full max-w-[1400px] items-center gap-4 px-4 py-3">
                <img
                    src={resolveThemeAssetUrl(askFrankLogo.url)}
                    alt="AskFrank"
                    draggable={false}
                    onClick={() => navigate(homePath())}
                    className="relative hidden cursor-pointer md:block"
                    style={{
                        left: askFrankLogo.offsetX,
                        top: askFrankLogo.offsetY,
                        width: 'auto',
                        height: 'auto',
                        maxWidth: 'none',
                        maxHeight: 'none',
                        objectFit: 'none',
                        flexShrink: 0,
                        imageRendering: 'pixelated',
                    }}
                />

                <form onSubmit={submit} className="flex min-w-0 flex-1 items-center gap-2">
                    <div className="relative min-w-0 flex-1">
                        <input
                            type="text"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            onKeyDown={(e) => onKeyDown(e, pick)}
                            onBlur={clear}
                            autoComplete="off"
                            maxLength={300}
                            placeholder="Ask Frank another question..."
                            aria-label="Ask Frank a question"
                            className="w-full border border-black/30 bg-white px-3 py-[6px] text-[14px] text-gray-800 outline-none focus:border-gold"
                            style={{ boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)' }}
                        />
                        <SuggestionList
                            suggestions={suggestions}
                            highlight={highlight}
                            onPick={pick}
                            onHighlight={setHighlight}
                        />
                    </div>
                    <button
                        type="submit"
                        className="shrink-0 border border-black/30 bg-maroon px-4 py-[6px] font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-white transition-transform hover:-translate-y-[1px] active:translate-y-[1px]"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.4)' }}
                    >
                        AskFrank!
                    </button>
                </form>

                {tiles.length > 0 && (
                    <div className="hidden w-[320px] shrink-0 grid-cols-3 grid-rows-2 gap-[6px] lg:grid">
                        {tiles.map(link => (
                            <RecommendedTile key={link.id} link={link} onOpen={openLink} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default FranklyNetBanner;
